import { useEffect } from 'react';

const SITE_NAME = 'BillPortal';
const DEFAULT_IMAGE = '/BP-Mark.png';

// Resolve the canonical/OG base URL. Prefer an explicit env, else the live origin.
export function siteBase() {
  const env = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');
  if (env) return env;
  try { return window.location.origin; } catch (e) { return ''; }
}

function upsertMeta(key, content, attr = 'name') {
  if (content === undefined || content === null || content === '') return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function upsertLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

// Full SEO head manager for a route: title, description, keywords, canonical,
// robots, Open Graph, Twitter cards and JSON-LD structured data.
export default function Seo({
  title,
  description,
  keywords,
  path,            // pathname for canonical (defaults to current location)
  image,           // absolute or root-relative OG image
  type = 'website',
  noindex = false,
  jsonLd,          // object or array of JSON-LD objects
  titleSuffix = true,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    const fullTitle = title ? (titleSuffix ? `${title} — ${SITE_NAME}` : title) : prevTitle;
    document.title = fullTitle;

    const base = siteBase();
    const p = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
    const canonical = base ? `${base}${p}` : p;
    const img = image ? (image.startsWith('http') ? image : `${base}${image}`) : `${base}${DEFAULT_IMAGE}`;

    upsertMeta('description', description);
    upsertMeta('keywords', Array.isArray(keywords) ? keywords.join(', ') : keywords);
    upsertMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    upsertLink('canonical', canonical);

    // Open Graph
    upsertMeta('og:site_name', SITE_NAME, 'property');
    upsertMeta('og:title', title || fullTitle, 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:type', type, 'property');
    upsertMeta('og:url', canonical, 'property');
    upsertMeta('og:image', img, 'property');

    // Twitter
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', title || fullTitle);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image', img);

    // JSON-LD structured data (tagged so we can clean up on unmount/change)
    const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    const scripts = blocks.filter(Boolean).map((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', '1');
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    };
  }, [title, description, keywords, path, image, type, noindex, jsonLd, titleSuffix]);

  return null;
}
